import type { Belief, JourneyPhase } from '@/types/brand'
import type {
  ArticleDoc,
  ContentChannel,
  ContentItem,
  ContentShape,
  ContentStatus,
  PostResults,
} from '@/types/content'
import type { Page } from '@/types/plan'
import { needsPerson, type PostRun } from '@/types/post-run'
import { isRunLive } from '@/types/setup-run'

/** The three board columns a piece sits in, derived from its status. */
export type BoardColumn = 'review' | 'scheduled' | 'published'

/** How far back the Published column reaches before "Show older". */
export const PUBLISHED_WINDOW_DAYS = 30

/** Which kinds of piece a view shows. */
export type PieceKind = 'all' | 'post' | 'article'

/** One post or article as the calendar shows it, joined to its topic, stage and numbers. */
export interface BoardPiece {
  readonly id: string
  readonly shape: ContentShape
  /** Where the piece goes out; `null` when none is set. */
  readonly channel: ContentChannel | null
  readonly status: ContentStatus
  readonly column: BoardColumn
  readonly title: string
  /** When it goes out (or went out, for a published piece); `null` when it has no date yet. */
  readonly date: Date | null
  /** The attached design; `null` when none is attached. */
  readonly designId: string | null
  /** Pages of the attached design; `0` when none is attached. */
  readonly pageCount: number
  /** Render version of the attached design; `null` when none is attached. */
  readonly renderVersion: number | null
  /** The brand's live post run is still checking this post. */
  readonly writing: boolean
  /** The check scored the post under the bar; `reasons` say why. */
  readonly needsPerson: boolean
  readonly score: number | null
  readonly reasons: readonly string[]
  readonly failed: boolean
  /** The error code of a failed post; `errorCopyKey` names its message. */
  readonly failedReason: string | null
  readonly hasBody: boolean
  readonly topicName: string | null
  readonly phase: JourneyPhase | null
  /** The belief the post argues; `null` for an article or a hand-made post. */
  readonly beliefSlug: string | null
  /** The question of the page an article answers; `null` for a post. */
  readonly question: string | null
  /** The post's numbers once published; `null` before then or while nothing is recorded. */
  readonly results: PostResults | null
}

/** Everything the board joins, as the stores hold it. */
export interface BoardSources {
  readonly items: readonly ContentItem[]
  readonly beliefs: readonly Belief[]
  readonly topics: readonly { readonly id: string; readonly name: string }[]
  readonly pages: readonly Pick<Page, 'id' | 'question'>[]
  /** The brand's latest post run, which is still writing the post it points at while live. */
  readonly latestRun: PostRun | null
  readonly articles: readonly ArticleDoc[]
  readonly results: readonly PostResults[]
}

/** The board column of a status: drafts and failed posts need review; `pending-handoff` is scheduled. */
export function boardColumnOf(status: ContentStatus): BoardColumn {
  switch (status) {
    case 'draft':
    case 'failed':
      return 'review'
    case 'scheduled':
    case 'pending-handoff':
      return 'scheduled'
    case 'published':
      return 'published'
  }
}

/** Joins every post and article to its topic, stage, page question and results. */
export function toBoardPieces(sources: BoardSources): BoardPiece[] {
  const phaseBySlug = new Map(sources.beliefs.map((belief) => [belief.slug, belief.phase]))
  const topicById = new Map(sources.topics.map((topic) => [topic.id, topic.name]))
  const questionByPageId = new Map(sources.pages.map((page) => [page.id, page.question]))
  const articleByPostId = new Map(sources.articles.map((article) => [article.postId, article]))
  const resultsByPostId = new Map(sources.results.map((result) => [result.postId, result]))
  const writingId = isRunLive(sources.latestRun) ? sources.latestRun?.postId : null

  return sources.items.map((item) => {
    const article = item.shape === 'article' ? articleByPostId.get(item.id) : undefined
    const column = boardColumnOf(item.status)
    const topicId = item.topicId ?? article?.topicId ?? null
    return {
      id: item.id,
      shape: item.shape,
      channel: item.channels[0] ?? null,
      status: item.status,
      column,
      title: item.title,
      date: column === 'published' ? (item.publishedAt ?? item.scheduledAt) : item.scheduledAt,
      designId: item.design?.designId ?? null,
      pageCount: item.design?.pageCount ?? 0,
      renderVersion: item.design?.renderVersion ?? null,
      writing: writingId !== null && writingId === item.id,
      needsPerson: needsPerson(item.score),
      score: item.score,
      reasons: item.reasons,
      failed: item.status === 'failed',
      failedReason: item.status === 'failed' ? (item.publishingError ?? null) : null,
      hasBody: item.copy.trim() !== '',
      topicName: topicId === null ? null : (topicById.get(topicId) ?? null),
      phase: item.beliefSlug === null ? null : (phaseBySlug.get(item.beliefSlug) ?? null),
      beliefSlug: item.beliefSlug,
      question: article ? (questionByPageId.get(article.pageId) ?? null) : null,
      results: column === 'published' ? (resultsByPostId.get(item.id) ?? null) : null,
    }
  })
}

/** The pieces of one kind; `all` keeps every piece. */
export function piecesOfKind(pieces: readonly BoardPiece[], kind: PieceKind): BoardPiece[] {
  return kind === 'all' ? [...pieces] : pieces.filter((piece) => piece.shape === kind)
}

/** How many pieces each kind holds. */
export function kindCounts(pieces: readonly BoardPiece[]): Record<PieceKind, number> {
  const articles = pieces.filter((piece) => piece.shape === 'article').length
  return { all: pieces.length, post: pieces.length - articles, article: articles }
}

function time(piece: BoardPiece): number {
  return piece.date?.getTime() ?? Number.POSITIVE_INFINITY
}

function reviewRank(piece: BoardPiece): number {
  if (piece.failed) return 0
  if (piece.writing) return 4
  if (piece.needsPerson) return 1
  return piece.date ? 2 : 3
}

/** Needs review order: failed first, then the ones that need a person, then by date, then undated, then the ones Raldo is writing. */
export function compareReview(a: BoardPiece, b: BoardPiece): number {
  return reviewRank(a) - reviewRank(b) || time(a) - time(b)
}

/** The board split into its columns, each in its own order. */
export interface BoardLayout {
  readonly review: { readonly ready: BoardPiece[]; readonly writing: BoardPiece[] }
  /** Soonest first. */
  readonly scheduled: BoardPiece[]
  /** Newest first, split at {@link PUBLISHED_WINDOW_DAYS} before `now`. */
  readonly published: { readonly recent: BoardPiece[]; readonly older: BoardPiece[] }
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Splits pieces into the board's columns and orders each one. */
export function layoutBoard(pieces: readonly BoardPiece[], now: Date): BoardLayout {
  const review = pieces.filter((piece) => piece.column === 'review').sort(compareReview)
  const scheduled = pieces
    .filter((piece) => piece.column === 'scheduled')
    .sort((a, b) => time(a) - time(b))
  const published = pieces
    .filter((piece) => piece.column === 'published')
    .sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0))
  const cutoff = now.getTime() - PUBLISHED_WINDOW_DAYS * DAY_MS
  return {
    review: {
      ready: review.filter((piece) => !piece.writing),
      writing: review.filter((piece) => piece.writing),
    },
    scheduled,
    published: {
      recent: published.filter((piece) => (piece.date?.getTime() ?? 0) >= cutoff),
      older: published.filter((piece) => (piece.date?.getTime() ?? 0) < cutoff),
    },
  }
}

/** Why a drag between columns does nothing. */
export type MoveRefusal =
  | 'neverPublishes'
  | 'publishedStays'
  | 'article'
  | 'writing'
  | 'noBody'
  | 'disconnected'

/** What dropping a piece on a column does. */
export type MoveVerdict =
  | { readonly kind: 'none' }
  | { readonly kind: 'schedule' }
  | { readonly kind: 'unschedule' }
  | { readonly kind: 'refuse'; readonly reason: MoveRefusal }

/** Judges a drop: review to scheduled schedules, scheduled to review unschedules, nothing ever publishes. */
export function judgeMove(
  piece: BoardPiece,
  to: BoardColumn,
  options: { readonly linkedInConnected: boolean },
): MoveVerdict {
  if (piece.column === to) return { kind: 'none' }
  if (to === 'published') return { kind: 'refuse', reason: 'neverPublishes' }
  if (piece.column === 'published') return { kind: 'refuse', reason: 'publishedStays' }
  if (to === 'review') return { kind: 'unschedule' }
  if (piece.shape === 'article') return { kind: 'refuse', reason: 'article' }
  if (piece.writing) return { kind: 'refuse', reason: 'writing' }
  if (!piece.hasBody) return { kind: 'refuse', reason: 'noBody' }
  if (!options.linkedInConnected) return { kind: 'refuse', reason: 'disconnected' }
  return { kind: 'schedule' }
}

/** The pieces with each in-flight move applied, so a dropped card shows in its new column at once. */
export function withPendingMoves(
  pieces: readonly BoardPiece[],
  moves: Readonly<Record<string, BoardColumn>>,
): BoardPiece[] {
  return pieces.map((piece) => {
    const column = moves[piece.id]
    return column === undefined || column === piece.column ? piece : { ...piece, column }
  })
}

/** One word for where a piece stands, for the calendar's cells and status marks. */
export type PieceStateKey =
  | 'writing'
  | 'failed'
  | 'needsPerson'
  | 'published'
  | 'scheduled'
  | 'awaitingArtwork'
  | 'draft'

/** The single state a piece shows on the calendar, most urgent first. */
export function pieceStateKey(piece: BoardPiece): PieceStateKey {
  if (piece.writing) return 'writing'
  if (piece.failed) return 'failed'
  if (piece.needsPerson && piece.column === 'review') return 'needsPerson'
  switch (piece.status) {
    case 'published':
      return 'published'
    case 'scheduled':
      return 'scheduled'
    case 'pending-handoff':
      return 'awaitingArtwork'
    default:
      return 'draft'
  }
}

const EXCEPTION_STATES: ReadonlySet<PieceStateKey> = new Set([
  'writing',
  'failed',
  'needsPerson',
  'awaitingArtwork',
])

/** Whether a state is an exception a list of its column still names: Raldo writing, a failure, a weak post or missing artwork. */
export function isExceptionState(state: PieceStateKey): boolean {
  return EXCEPTION_STATES.has(state)
}

/** Where a date sits against today: one of the three named days, or `null` for any other. */
export type RelativeDay = 'yesterday' | 'today' | 'tomorrow'

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

/** Names `date` relative to `now` when it is yesterday, today or tomorrow; `null` otherwise. */
export function relativeDay(date: Date, now: Date): RelativeDay | null {
  const diff = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS)
  if (diff === -1) return 'yesterday'
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  return null
}
